import { useMemo } from 'react';

import { ImageTypeMetadata } from '../types/imageTypeMetadata';
import { LengthRange } from '../types/lengthRange';

type InternalResolutionChunk = { resolution: number; density: number; size: number };

/***
 * Calculates a series of chunks for all resolutions, and the current best resolution for zoom scale
 * @param range range of the row
 * @param scale scale of the row
 * @param imageTypesMetadata metadata for the
 */
const useChunkedImageRanges = (
	range: LengthRange,
	scale: number,
	imageTypesMetadata: (ImageTypeMetadata | undefined)[],
	overviewResolutions: Record<number, number | undefined>,
): {
	chunks: Record<number, Record<number, LengthRange[]>>;
	bestResolution?: Record<number, number>;
	isOverviewOnly: Record<number, boolean>;
} => {
	// * Initialise to highest available resolution
	// * Then find lowest resolution closest to provided scale
	const bestResolution = useMemo<Record<number, number>>(() => {
		return imageTypesMetadata.reduce((prevMap, currMeta) => {
			if (!currMeta) return prevMap;
			const overviewRes = overviewResolutions[currMeta.id] ?? 0;
			return {
				...prevMap,
				[currMeta.id]: currMeta.resolutions
					.filter((res) => res >= overviewRes)
					.sort((a, b) => b - a)
					.reduce<number | undefined>(
						(previousValue, currentValue) =>
							currentValue && currentValue >= scale ? currentValue : previousValue,
						Math.max(...currMeta.resolutions),
					),
			};
		}, {});
	}, [scale, imageTypesMetadata, overviewResolutions]);

	// When the scale is far out use the composite overview chunks
	const isOverviewOnly = useMemo<Record<number, boolean>>(
		() =>
			imageTypesMetadata.reduce((prev, curr) => {
				if (curr) {
					const overviewRes = overviewResolutions[curr.id];
					// If overviewRes is undefined, then the overview is still loading
					return { ...prev, [curr.id]: overviewRes ? overviewRes > scale : true };
				}
				return prev;
			}, {}),
		[imageTypesMetadata, scale, overviewResolutions],
	);

	const resolutionChunks: Record<number, InternalResolutionChunk[]> = useMemo(
		() =>
			imageTypesMetadata.reduce((prevMap, currMeta) => {
				if (!currMeta) return prevMap;
				return {
					...prevMap,
					[currMeta.id]: currMeta.resolutions.reduce<InternalResolutionChunk[]>((previous, resolution) => {
						const density =
							(resolution ?? 1000) < 1000
								? 1 / Math.ceil((1000 / (resolution ?? 1000)) * 2)
								: Math.ceil(1000 / (resolution ?? 1000)) * 2;
						return [...previous, { resolution, density, size: 1 / density }];
					}, []),
				};
			}, {}),
		[imageTypesMetadata],
	);

	// Get the boundaries of the outer boundary chunks
	const chunksRange = useMemo<Record<number, Record<string, LengthRange>>>(() => {
		if (!resolutionChunks) return {};
		return Object.keys(resolutionChunks).reduce((previousMap, key) => {
			const currChunks = resolutionChunks[+key];
			return {
				...previousMap,
				[key]: currChunks.reduce((previous, current) => {
					return {
						...previous,
						[current.resolution]: {
							depthFrom: (1.0 / current.density) * Math.floor(current.density * range.depthFrom),
							depthTo: (1.0 / current.density) * Math.floor(current.density * range.depthTo + 1.0),
						},
					};
				}, {}),
			};
		}, {});
	}, [resolutionChunks, range.depthFrom, range.depthTo]);

	// Get each chunk range
	const chunks = useMemo<Record<number, Record<number, LengthRange[]>>>(() => {
		if (!resolutionChunks) return {};
		return imageTypesMetadata.reduce((prevMap, currMeta) => {
			if (!currMeta) return prevMap;
			return {
				...prevMap,
				[currMeta.id]: currMeta.resolutions
					.sort((a, b) => a - b)
					.reduce((previousValue, resolution) => {
						const chunkRes = resolutionChunks[currMeta.id].find((value) => value.resolution === resolution);
						const imageTypeChunks = chunksRange[currMeta.id];
						const chunkRange = imageTypeChunks[resolution];
						if (!chunkRes || !chunkRange) return previousValue;
						const chunkSize = chunkRes.size;
						if (!chunkSize) return previousValue;
						const arr: number[] = [];
						let pos =
							chunkRange.depthFrom <= range.depthFrom
								? chunkRange.depthFrom
								: chunkRange.depthFrom - chunkSize;
						const end =
							chunkRange.depthTo >= range.depthTo ? range.depthTo : chunkRange.depthTo + chunkSize;
						while (pos < end) {
							if (pos < (currMeta.trayTo ?? currMeta.depthTo) && pos + chunkSize > currMeta.depthFrom) {
								arr.push(pos);
							}
							pos += chunkSize;
						}
						return {
							...previousValue,
							[resolution]: arr.map((value) => ({
								depthFrom: Math.max(currMeta.depthFrom, value),
								depthTo: Math.min(currMeta.trayTo ?? currMeta.depthTo, value + chunkSize),
							})),
						};
					}, {}),
			};
		}, {});
	}, [resolutionChunks, imageTypesMetadata, chunksRange, range.depthFrom, range.depthTo]);

	return { chunks, bestResolution, isOverviewOnly };
};

export default useChunkedImageRanges;
